import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { EmailInviteInputCard } from "../components/EmailInviteInputCard";
import { InviteCodeInputCard } from "../components/InviteCodeInputCard";
import { GOOGLE_CLIENT_ID, GOOGLE_SSO_BYPASS } from "../lib/config";
import { AppError, logError, toUserMessage } from "../lib/errors";
import { api } from "../lib/api";
import { storage } from "../lib/storage";
import { useSsoFlow, type SsoLoginResponse } from "../hooks/useSsoFlow";
import { bootstrapAuth, type BootstrapPhase } from "../services/auth";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (element: HTMLElement, options: object) => void;
        };
      };
    };
  }
}

type Phase =
  | "initializing"
  | "googleSignIn"
  | "googleVerifying"
  | BootstrapPhase
  | "navigating"
  | "error"
  | "forbidden";

type EntryMode = "default" | "emailInvite";

const GOOGLE_GSI_SCRIPT_SELECTOR = 'script[data-google-gsi-script="true"]';
const FORCE_NEW_USER_BOOTSTRAP_KEY = "wizmate.force-new-user-bootstrap";
const FORCE_NEW_USER_BOOTSTRAP_TTL_MS = 5 * 60 * 1000;
const NON_PERSONALIZED_GOOGLE_BUTTON_WIDTH_PX = "196";

let googleGsiLoader: Promise<void> | null = null;

function getGoogleAccountsId() {
  return window.google?.accounts?.id ?? null;
}

function loadGoogleIdentityServices(): Promise<void> {
  if (getGoogleAccountsId()) {
    return Promise.resolve();
  }

  if (!googleGsiLoader) {
    googleGsiLoader = new Promise((resolve, reject) => {
      let script = document.querySelector<HTMLScriptElement>(GOOGLE_GSI_SCRIPT_SELECTOR);

      if (script?.dataset.loaded === "error") {
        script.remove();
        script = null;
      }

      const targetScript = script ?? document.createElement("script");

      const cleanup = () => {
        targetScript.removeEventListener("load", handleLoad);
        targetScript.removeEventListener("error", handleError);
      };

      const fail = (message: string) => {
        cleanup();
        googleGsiLoader = null;
        reject(new Error(message));
      };

      const handleLoad = () => {
        targetScript.dataset.loaded = "true";
        cleanup();
        if (getGoogleAccountsId()) {
          resolve();
          return;
        }
        googleGsiLoader = null;
        reject(new Error("Google Identity Services did not initialize."));
      };

      const handleError = () => {
        targetScript.dataset.loaded = "error";
        fail("Failed to load Google Identity Services.");
      };

      if (getGoogleAccountsId()) {
        resolve();
        return;
      }

      if (targetScript.dataset.loaded === "true") {
        fail("Google Identity Services did not initialize.");
        return;
      }

      targetScript.addEventListener("load", handleLoad);
      targetScript.addEventListener("error", handleError);

      if (!script) {
        targetScript.src = "https://accounts.google.com/gsi/client";
        targetScript.async = true;
        targetScript.defer = true;
        targetScript.dataset.googleGsiScript = "true";
        targetScript.dataset.loaded = "loading";
        document.head.appendChild(targetScript);
      }
    });
  }

  return googleGsiLoader;
}

function consumeForceNewUserBootstrapFlag(): boolean {
  const raw = localStorage.getItem(FORCE_NEW_USER_BOOTSTRAP_KEY);
  if (!raw) return false;

  localStorage.removeItem(FORCE_NEW_USER_BOOTSTRAP_KEY);

  const requestedAt = Number(raw);
  if (!Number.isFinite(requestedAt)) return false;

  return Date.now() - requestedAt <= FORCE_NEW_USER_BOOTSTRAP_TTL_MS;
}

export default function Splash() {
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>("initializing");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [entryMode, setEntryMode] = useState<EntryMode>("default");
  const [emailInviteError, setEmailInviteError] = useState<string | null>(null);
  const [emailInviteLoading, setEmailInviteLoading] = useState(false);

  const runningRef = useRef(false);
  const googleBtnRef = useRef<HTMLDivElement>(null);

  const runBootstrap = useCallback(async () => {
    try {
      setErrorMessage(null);
      await bootstrapAuth({
        onPhase: (currentPhase: BootstrapPhase) => setPhase(currentPhase),
      });
      setPhase("navigating");
      navigate("/home", { replace: true });
    } catch (errorValue) {
      if (errorValue instanceof AppError && errorValue.code === "SESSION_EXPIRED") {
        storage.clearGoogleSso();
        setPhase("googleSignIn");
        runningRef.current = false;
        return;
      }
      logError(errorValue);
      setPhase("error");
      setErrorMessage(toUserMessage(errorValue));
      runningRef.current = false;
    }
  }, [navigate]);

  const handleSsoSuccess = useCallback(
    async (ssoResponse: SsoLoginResponse) => {
      const { email, userId, secretKey, accessToken } = ssoResponse;
      storage.setGoogleSso(email);

      const shouldForceNewUserBootstrap = consumeForceNewUserBootstrapFlag();
      const shouldSkipSsoSessionRestore =
        shouldForceNewUserBootstrap && ssoResponse.isNewUser === true;

      if (!shouldSkipSsoSessionRestore && userId && accessToken) {
        const currentAuth = storage.getAuth();
        const isSameUser = currentAuth.userId === userId;
        const effectiveSecretKey = secretKey ?? (isSameUser ? currentAuth.secretKey : undefined);

        storage.setAuth({
          userId,
          secretKey: effectiveSecretKey,
          accessToken,
          currentCharacterId: isSameUser ? currentAuth.currentCharacterId : undefined,
          currentVoiceId: isSameUser ? currentAuth.currentVoiceId : undefined,
          terminalIdentificationNumber: currentAuth.terminalIdentificationNumber,
        });
      }

      await runBootstrap();
    },
    [runBootstrap]
  );

  const {
    ssoFlowPhase,
    inviteCodeError,
    inviteCodeLoading,
    startSsoLogin,
    submitInviteCode,
    cancelInviteCode,
  } = useSsoFlow(handleSsoSuccess);

  const handleGoogleCredential = useCallback(
    async (credential: string) => {
      if (runningRef.current) return;
      runningRef.current = true;

      setPhase("googleVerifying");
      setErrorMessage(null);

      try {
        const inviteCodeRequired = await startSsoLogin(credential);
        if (inviteCodeRequired) {
          setPhase("googleSignIn");
        }
      } catch (errorValue) {
        if (errorValue instanceof AppError && errorValue.code === "FORBIDDEN") {
          setPhase("googleSignIn");
          setErrorMessage("このアカウントではログインできません。");
        } else {
          logError(errorValue);
          setPhase("googleSignIn");
          setErrorMessage(toUserMessage(errorValue));
        }
        runningRef.current = false;
      }
    },
    [startSsoLogin]
  );

  const handleCancelInviteCode = useCallback(() => {
    cancelInviteCode();
    runningRef.current = false;
  }, [cancelInviteCode]);

  const handleStartEmailInvite = useCallback(() => {
    setEntryMode("emailInvite");
    setErrorMessage(null);
    setEmailInviteError(null);
  }, []);

  const handleCancelEmailInvite = useCallback(() => {
    if (emailInviteLoading) return;
    setEntryMode("default");
    setEmailInviteError(null);
  }, [emailInviteLoading]);

  const handleEmailInviteSubmit = useCallback(
    async (params: { email: string; inviteCode: string }) => {
      if (emailInviteLoading) return;

      setEmailInviteLoading(true);
      setEmailInviteError(null);
      try {
        const response = await api.emailInviteLogin({
          email: params.email,
          inviteCode: params.inviteCode,
        });

        const currentAuth = storage.getAuth();
        storage.clearGoogleSso();
        storage.setAuth({
          userId: response.userId,
          secretKey: undefined,
          accessToken: response.accessToken,
          currentCharacterId: undefined,
          currentVoiceId: undefined,
          terminalIdentificationNumber: currentAuth.terminalIdentificationNumber,
        });

        navigate("/onboarding", { replace: true });
      } catch (errorValue) {
        setEmailInviteError(toUserMessage(errorValue));
      } finally {
        setEmailInviteLoading(false);
      }
    },
    [emailInviteLoading, navigate]
  );

  useEffect(() => {
    if (GOOGLE_SSO_BYPASS) {
      runningRef.current = true;
      void runBootstrap();
      return;
    }

    if (storage.isGoogleSsoValid()) {
      runningRef.current = true;
      void runBootstrap();
      return;
    }

    if (!GOOGLE_CLIENT_ID) {
      setPhase("googleSignIn");
      setErrorMessage("Google Client ID が設定されていません。");
      return;
    }

    setPhase("googleSignIn");
  }, [runBootstrap]);

  useEffect(() => {
    if (phase !== "googleSignIn" || !GOOGLE_CLIENT_ID) {
      return;
    }

    let disposed = false;

    const renderGoogleButton = async () => {
      try {
        await loadGoogleIdentityServices();

        if (disposed) return;

        const googleAccountsId = getGoogleAccountsId();
        const buttonContainer = googleBtnRef.current;
        if (!googleAccountsId || !buttonContainer) return;

        buttonContainer.innerHTML = "";
        googleAccountsId.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: ({ credential }) => {
            void handleGoogleCredential(credential);
          },
        });
        googleAccountsId.renderButton(buttonContainer, {
          theme: "outline",
          size: "large",
          width: NON_PERSONALIZED_GOOGLE_BUTTON_WIDTH_PX,
          locale: "ja",
        });
      } catch (errorValue) {
        if (disposed) return;
        logError(errorValue);
        setPhase("googleSignIn");
        setErrorMessage(
          "Google でログインする準備に失敗しました。ページを読み直して、もう一度お試しください。"
        );
        runningRef.current = false;
      }
    };

    void renderGoogleButton();

    return () => {
      disposed = true;
    };
  }, [phase, handleGoogleCredential]);

  const onInitialize = async () => {
    const ok = window.confirm("初期化します。よろしいですか？");
    if (!ok) return;

    const auth = storage.getAuth();
    if (auth.userId && auth.accessToken) {
      try {
        await api.invalidateGoogleSso({ reason: "user_initialize" });
      } catch (errorValue) {
        logError(errorValue);
      }
    }

    storage.clearAuth();
    storage.clearGoogleSso();
    localStorage.removeItem("app_device_id");
    localStorage.setItem(FORCE_NEW_USER_BOOTSTRAP_KEY, String(Date.now()));

    window.location.replace("/splash");
  };

  const handleRetry = () => {
    if (phase !== "error") return;
    runningRef.current = true;
    void runBootstrap();
  };

  const phaseLabel =
    phase === "googleVerifying"
      ? "認証を確認しています..."
      : phase === "creatingUser"
        ? "ユーザーを準備しています..."
        : phase === "loggingIn"
          ? "ログインしています..."
          : phase === "eventLogin"
            ? "ログイン情報を反映しています..."
            : phase === "checking"
              ? "確認しています..."
              : phase === "navigating"
                ? "移動しています..."
                : "";

  const isLoading =
    phase === "initializing" ||
    phase === "googleVerifying" ||
    phase === "checking" ||
    phase === "creatingUser" ||
    phase === "loggingIn" ||
    phase === "eventLogin" ||
    phase === "navigating";

  const canInitialize = phase !== "navigating";

  return (
    <main className="flex min-h-screen select-none items-center justify-center bg-[#FDF8F5] px-6 text-[#4A3E39] antialiased">
      <div className="w-full max-w-sm">
        <div className="relative overflow-hidden rounded-[2.2rem] border border-[#EADBC8] bg-white/70 shadow-[0_14px_28px_rgba(166,124,82,0.16)] backdrop-blur-sm">
          <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-[#F4E3D7] opacity-70 blur-3xl" />

          <div className="absolute right-4 top-4 flex gap-2">
            {canInitialize ? (
              <button
                type="button"
                className="rounded-full border border-[#EADBC8] bg-white/80 px-3 py-1 text-[11px] font-bold text-[#A67C52]"
                onClick={onInitialize}
                aria-label="Initialize"
              >
                初期化
              </button>
            ) : null}
          </div>

          <div className="px-8 pb-10 pt-12 text-center">
            <p className="text-[11px] font-bold tracking-[0.12em] text-[#A67C52]">PERSONAL CONCIERGE</p>
            <h1 className="mt-1 text-3xl font-black tracking-tight text-[#7C5A4E]">WizMate</h1>

            <div className="mx-auto mb-6 mt-6 h-40 w-40 overflow-hidden rounded-full border-[6px] border-white bg-white shadow-[0_10px_25px_rgba(166,124,82,0.2)]">
              <img src="/img/logo.jpg" alt="WizMate Logo" className="h-full w-full object-cover object-center" />
            </div>

            <p className="text-sm font-medium text-[#7B6861]">あなた専属コンシェルジュを起動します</p>

            {phase === "googleSignIn" && ssoFlowPhase === "inviteCodeInput" ? (
              <InviteCodeInputCard
                onSubmit={submitInviteCode}
                onCancel={handleCancelInviteCode}
                error={inviteCodeError}
                loading={inviteCodeLoading}
              />
            ) : null}

            {phase === "googleSignIn" && ssoFlowPhase === "idle" && entryMode === "emailInvite" ? (
              <EmailInviteInputCard
                onSubmit={handleEmailInviteSubmit}
                onCancel={handleCancelEmailInvite}
                error={emailInviteError}
                loading={emailInviteLoading}
              />
            ) : null}

            {phase === "googleSignIn" && ssoFlowPhase === "idle" && entryMode === "default" ? (
              <div className="mt-10">
                {errorMessage ? (
                  <p className="mb-4 rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-xs font-medium text-[#A1493E]">
                    {errorMessage}
                  </p>
                ) : null}
                <p className="mb-4 text-sm font-medium text-[#7B6861]">Googleアカウントでサインインしてください</p>
                <div className="flex justify-center">
                  <div ref={googleBtnRef} />
                </div>
                <div className="mt-5 flex flex-col items-center gap-2">
                  <button
                    type="button"
                    className="rounded-xl border border-[#EADBC8] bg-[#FFF8F4] px-4 py-3 text-sm font-bold text-[#7C5A4E] transition hover:bg-[#F8EEE7] active:scale-[0.99]"
                    onClick={handleStartEmailInvite}
                  >
                    メールアドレスで始める
                  </button>
                  <p className="max-w-[18rem] text-center text-xs leading-5 text-[#9C867D]">
                    Google アカウントを使わずに、招待コード付きのメールアドレス登録から始められます。
                  </p>
                </div>
              </div>
            ) : null}

            {isLoading ? (
              <div className="mt-10 rounded-2xl border border-[#EADBC8] bg-white/70 px-4 py-4">
                <p className="text-sm font-bold text-[#5D4D46]">{phaseLabel}</p>
                <div className="mt-4 flex items-center justify-center gap-1.5" aria-label="loading">
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#D1A48F] [animation-delay:-0.2s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#D1A48F] [animation-delay:-0.1s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#D1A48F]" />
                </div>
              </div>
            ) : null}

            {phase === "forbidden" ? (
              <div className="mt-8 rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-4 text-left">
                <p className="text-sm font-bold text-[#A1493E]">このアカウントでは利用できません</p>
                <p className="mt-1 text-xs text-[#A1493E]">{errorMessage}</p>
                <p className="mt-3 text-xs font-medium text-[#6B5851]">
                  アクセス条件については運営までお問い合わせください。
                </p>
              </div>
            ) : null}

            {phase === "error" ? (
              <div className="mt-8 rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-4 text-left">
                <p className="text-sm font-bold text-[#A1493E]">通信に失敗しました</p>
                <p className="mt-1 break-words text-xs text-[#A1493E]">{errorMessage}</p>
                <button
                  className="mt-3 w-full rounded-xl border border-[#EADBC8] bg-white px-4 py-2 text-sm font-bold text-[#5D4D46] hover:bg-[#FDF8F5] active:scale-[0.99]"
                  onClick={handleRetry}
                >
                  再試行
                </button>
                <button
                  type="button"
                  className="mt-2 w-full rounded-xl border border-[#EADBC8] bg-[#FFF8F4] px-4 py-2 text-sm font-bold text-[#7C5A4E] transition hover:bg-[#F8EEE7] active:scale-[0.99]"
                  onClick={handleStartEmailInvite}
                >
                  メールアドレスで始める
                </button>
              </div>
            ) : null}
          </div>

          <div className="border-t border-[#EADBC8] px-6 py-4 text-center">
            <p className="text-[11px] text-[#9C867D]">© WizMate</p>
          </div>
        </div>
      </div>
    </main>
  );
}
